import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Header } from '../../Header';
import { useAuth } from '../../../AuthProvider';
import { getBatchStatus, getBatchResults, downloadBatchZipStream } from '../../../api/virtualTryonClient';
import { saveAs } from 'file-saver';
import { 
  ArrowLeft,
  Download, 
  CheckCircle, 
  XCircle, 
  Loader, 
  RefreshCw,
  Image as ImageIcon,
  FileArchive
} from 'lucide-react';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

export const BatchDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { batchId: encodedBatchId } = useParams();
  const { currentUser } = useAuth();
  
  // Decode the batch ID from URL
  const batchId = decodeURIComponent(encodedBatchId);
  
  const [batchStatus, setBatchStatus] = useState(null);  // Batch metadata
  const [batchResults, setBatchResults] = useState(null);  // Paginated results
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingIndividual, setDownloadingIndividual] = useState({});

  const fetchBatchData = async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      if (!currentUser) return;

      const headers = {
        Authorization: `Bearer ${await currentUser.getIdToken()}`,
        "X-User-Id": currentUser.uid,
      };

      // Fetch batch status (metadata only)
      const statusResponse = await getBatchStatus({ batchId, headers });
      setBatchStatus(statusResponse);

      // Only fetch results if they're ready
      if (statusResponse.results_ready) {
        // Fetch all results (paginated) - for now get first 100 results
        // TODO: Implement pagination UI if needed for large batches
        const resultsResponse = await getBatchResults({ 
          batchId, 
          headers, 
          page: 1, 
          limit: 100 
        });
        setBatchResults(resultsResponse);
      }

      // Auto-download if coming from batch status page
      if (location.state?.autoDownload && statusResponse.status === 'completed') {
        handleDownloadAll();
        // Clear the state
        window.history.replaceState({}, document.title);
      }
    } catch (err) {
      console.error('Error fetching batch data:', err);
      setError(err.message || 'Failed to load batch data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBatchData();

    // Auto-refresh every 5 seconds if batch is still processing
    const interval = setInterval(() => {
      if (batchStatus && (batchStatus.status === 'pending' || batchStatus.status === 'processing')) {
        fetchBatchData(true);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [batchId, currentUser]);

  const downloadImage = async (url, filename) => {
    return new Promise((resolve, reject) => {
      try {
        console.log('[Download] Starting download:', filename, url);
        // Create a temporary anchor element for download
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        console.log('[Download] Download triggered successfully');
        // Small delay to ensure download starts
        setTimeout(() => resolve(), 100);
      } catch (err) {
        console.error('[Download] Error downloading image:', err);
        reject(err);
      }
    });
  };

  const handleDownloadIndividual = async (job, index) => {
    console.log('[Download Individual] Called with:', { job, index });
    if (!job.result_url) {
      console.warn('[Download Individual] No result_url found');
      return;
    }

    try {
      setDownloadingIndividual(prev => ({ ...prev, [job.request_key]: true }));
      const batchIdShort = batchId ? batchId.substring(0, 8) : 'unknown';
      const filename = `batch_${batchIdShort}_job_${index + 1}.png`;
      console.log('[Download Individual] Downloading:', filename);
      await downloadImage(job.result_url, filename);
      console.log('[Download Individual] Success');
    } catch (err) {
      console.error('[Download Individual] Error:', err);
      setError('Failed to download image');
    } finally {
      setDownloadingIndividual(prev => ({ ...prev, [job.request_key]: false }));
    }
  };

  const handleDownloadAll = async () => {
    console.log('[Download All] Called - using batch-download-stream endpoint');
    if (!batchResults || !batchResults.results) {
      console.warn('[Download All] No batch results');
      return;
    }

    const successfulJobs = batchResults.results.filter(job => job.status === 'completed' && job.result_url);
    console.log('[Download All] Found', successfulJobs.length, 'successful jobs');
    
    if (successfulJobs.length === 0) {
      setError('No images available for download');
      return;
    }

    try {
      setDownloading(true);
      setError(null);

      // Get auth token
      const authToken = await currentUser.getIdToken();
      const batchIdShort = batchId ? batchId.substring(0, 8) : 'unknown';

      console.log('[Download All] Downloading ZIP stream from backend...');
      console.log('[Download All] Batch ID:', batchId);
      
      // Use the new streaming endpoint - much more efficient!
      const zipBlob = await downloadBatchZipStream({
        batchId,
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'x-user-id': currentUser.uid,
        }
      });

      console.log('[Download All] ZIP received, size:', zipBlob.size);
      
      // Save the ZIP file
      saveAs(zipBlob, `batch_${batchIdShort}_results.zip`);
      console.log('[Download All] ZIP download triggered');

    } catch (err) {
      console.error('[Download All] Error:', err);
      setError(`Failed to download ZIP file: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <Loader className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </div>
      </div>
    );
  }

  if (error && !batchStatus) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Error loading batch details</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                <button
                  onClick={() => navigate('/batch-status')}
                  className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Back to Batch Status
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const successfulJobs = batchResults?.results?.filter(j => j.status === 'completed' && j.result_url) || [];
  const failedJobs = batchResults?.results?.filter(j => j.status === 'failed') || [];
  const pendingJobs = batchResults?.results?.filter(j => j.status === 'pending' || j.status === 'processing') || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={() => navigate('/batch-status')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Batch Jobs</span>
            <span className="sm:hidden">Back</span>
          </button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Batch Details</h1>
              <p className="hidden sm:block text-gray-600 mt-1 font-mono text-xs sm:text-sm truncate" title={batchId}>ID: {batchId}</p>
            </div>
            
            <div className="hidden sm:flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <button
                onClick={() => fetchBatchData(true)}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
              
              {successfulJobs.length > 0 && (
                <button
                  onClick={handleDownloadAll}
                  disabled={downloading}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {downloading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Creating ZIP...
                    </>
                  ) : (
                    <>
                      <FileArchive className="w-4 h-4" />
                      Download All ({successfulJobs.length})
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Status Summary - Hidden on mobile */}
        <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Jobs</p>
                <p className="text-2xl font-bold text-gray-900">{batchStatus?.total_jobs || 0}</p>
              </div>
              <ImageIcon className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600">{successfulJobs.length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-red-600">{failedJobs.length}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Processing</p>
                <p className="text-2xl font-bold text-blue-600">{pendingJobs.length}</p>
              </div>
              <Loader className={`w-8 h-8 text-blue-400 ${pendingJobs.length > 0 ? 'animate-spin' : ''}`} />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Batch Status - Hidden on mobile */}
        <div className="hidden sm:block mb-6 sm:mb-8 bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Batch Status</h2>
          <div className="flex items-center gap-4">
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${STATUS_COLORS[batchStatus?.status]}`}>
              {batchStatus?.status?.toUpperCase()}
            </span>
            <div className="flex-1">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Progress</span>
                <span>{successfulJobs.length} / {batchStatus?.total_jobs || 0}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((successfulJobs.length / (batchStatus?.total_jobs || 1)) * 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Results Grid */}
        {successfulJobs.length > 0 && (
          <div className="mb-6 sm:mb-8">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Generated Images</h2>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {successfulJobs.map((job, index) => (
                <div key={job.request_key} className="bg-white rounded-lg shadow-sm overflow-hidden group">
                  <div className="relative aspect-square">
                    <img
                      src={job.result_url}
                      alt={`Result ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {/* Download button - visible on mobile, hover on desktop */}
                    <div className="absolute inset-0 flex items-end justify-center p-2 sm:items-center sm:p-0 transition-all sm:bg-black/0 sm:group-hover:bg-black/40 z-10">
                      <button
                        onClick={() => handleDownloadIndividual(job, index)}
                        disabled={downloadingIndividual[job.request_key]}
                        className="sm:opacity-0 sm:group-hover:opacity-100 transition-opacity px-3 py-2 bg-white text-gray-900 rounded-lg hover:bg-gray-100 flex items-center gap-2 text-xs sm:text-sm shadow-lg relative z-20"
                        aria-label={`Download image ${index + 1}`}
                      >
                        {downloadingIndividual[job.request_key] ? (
                          <>
                            <Loader className="w-4 h-4 animate-spin" />
                            <span className="hidden sm:inline">Downloading...</span>
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            <span className="hidden sm:inline">Download</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="p-2 sm:p-3">
                    <p className="text-xs text-gray-500 font-mono truncate">
                      {job.request_key ? `${job.request_key.substring(0, 16)}...` : 'N/A'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Failed Jobs - Hidden on mobile */}
        {failedJobs.length > 0 && (
          <div className="hidden sm:block">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Failed Jobs</h2>
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {failedJobs.map((job) => (
                    <tr key={job.request_key}>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-900">
                        {job.request_key ? `${job.request_key.substring(0, 16)}...` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-red-600">
                        {job.error || 'Unknown error'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        )}

        {/* Pending Jobs - Hidden on mobile */}
        {pendingJobs.length > 0 && (
          <div className="hidden sm:block mt-6 sm:mt-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Processing Jobs</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 flex items-center gap-4">
              <Loader className="w-6 h-6 animate-spin text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  {pendingJobs.length} job{pendingJobs.length > 1 ? 's' : ''} still processing...
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  This page will auto-refresh every 5 seconds
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchDetails;
